import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, RefreshCw, ShieldCheck } from 'lucide-react';
import { useCurrency } from '@/lib/useCurrency';
import api from '@/lib/api';

export default function ReviewQueue() {
  const { symbol: cs } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);

  const load = async (reason = null) => {
    setLoading(true);
    try {
      const url = reason
        ? `/analytics/review-queue?reason=${encodeURIComponent(reason)}`
        : '/analytics/review-queue';
      const res = await api.get(url);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onTabChange = (val) => {
    const next = val === 'all' ? null : val;
    setSelectedReason(next);
    load(next);
  };

  const fmt = (v) => `${cs}${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <div className="p-6 space-y-4" data-testid="review-queue-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Review Queue</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Rows flagged by DataRefinery as <code className="text-xs bg-muted px-1 py-0.5 rounded">needs_review=true</code>.
            Fix these in DataRefinery and re-upload — SpendLens does not modify source data.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => load(selectedReason)} disabled={loading} data-testid="refresh-button">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      {!data ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">Loading…</CardContent></Card>
      ) : !data.has_data ? (
        <Card className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20" data-testid="review-queue-empty">
          <CardHeader className="flex flex-row items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-emerald-600 mt-0.5" />
            <div>
              <CardTitle className="text-emerald-900 dark:text-emerald-100">Nothing to review</CardTitle>
              <CardDescription>
                No records in your current dataset are flagged as <code className="text-xs">needs_review=true</code>.
                Either DataRefinery resolved everything cleanly, or your upload pre-dates the review-flag feature.
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      ) : (
        <>
          {/* Reason tabs */}
          <Card data-testid="review-reason-tabs">
            <CardContent className="py-3">
              <Tabs value={selectedReason || 'all'} onValueChange={onTabChange}>
                <TabsList className="flex-wrap h-auto">
                  <TabsTrigger value="all" data-testid="reason-tab-all">
                    All <Badge variant="secondary" className="ml-2">{data.total}</Badge>
                  </TabsTrigger>
                  {data.groups.map((g) => (
                    <TabsTrigger key={g.reason} value={g.reason} data-testid={`reason-tab-${g.reason}`}>
                      {g.title}
                      <Badge variant="secondary" className="ml-2">{g.count}</Badge>
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </CardContent>
          </Card>

          {/* Reason explanation */}
          {selectedReason && (() => {
            const g = data.groups.find(x => x.reason === selectedReason);
            if (!g) return null;
            return (
              <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/20" data-testid="reason-explanation">
                <CardHeader className="flex flex-row items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5 shrink-0" />
                  <div>
                    <CardTitle className="text-amber-900 dark:text-amber-100">{g.title}</CardTitle>
                    <CardDescription className="mt-1"><strong>Why:</strong> {g.why}</CardDescription>
                    <CardDescription className="mt-1"><strong>How to fix:</strong> {g.remediation}</CardDescription>
                  </div>
                </CardHeader>
              </Card>
            );
          })()}

          {/* Table */}
          <Card data-testid="review-queue-table">
            <CardHeader>
              <CardTitle>Sample of flagged rows</CardTitle>
              <CardDescription>Showing up to 200 rows. Fix the root cause in DataRefinery; SpendLens updates automatically on re-upload.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Supplier</th>
                      <th className="text-left py-2 px-2">Invoice</th>
                      <th className="text-left py-2 px-2">Date</th>
                      <th className="text-left py-2 px-2">L1 / L2 / L3</th>
                      <th className="text-left py-2 px-2">Review reason</th>
                      <th className="text-right py-2 px-2">Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.length === 0 ? (
                      <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No rows</td></tr>
                    ) : data.rows.map((r, i) => (
                      <tr key={i} className="border-b hover:bg-muted/50" data-testid={`review-row-${i}`}>
                        <td className="py-1.5 px-2 font-medium">{r.supplier_name || '-'}</td>
                        <td className="py-1.5 px-2 font-mono text-xs">{r.invoice_number || '-'}</td>
                        <td className="py-1.5 px-2">{r.invoice_date || '-'}</td>
                        <td className="py-1.5 px-2 text-muted-foreground text-xs">
                          {[r.category_level_1, r.category_level_2, r.category_level_3].filter(Boolean).join(' / ') || '-'}
                        </td>
                        <td className="py-1.5 px-2">
                          <Badge variant="outline" className="text-xs">{r.review_reason || 'unspecified'}</Badge>
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono">{fmt(r.spend_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
